import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { clearMenuCache } from '../../../../shared/hooks/useMenu.js';
import {
  listRoles,
  listPermissions,
  getRolePermissions,
  assignPermission,
  revokePermission,
  copyRoleRights,
  type Role,
  type Permission,
} from '../../api/rolesApi.js';
import {
  listMenus,
  listRoleMenus,
  assignMenuRole,
  revokeMenuRole,
  type MenuItem,
} from '../../api/menusApi.js';

/**
 * RoleRightsPage — pick a role, tick what it may do, save.
 *
 * Two things are granted here and they are not the same:
 *   • menus  (T019) decide what appears in the menu bar
 *   • rights (T017) decide what the API will actually allow
 * A menu without its right opens and then 403s, so both live on one screen.
 *
 * Nothing is hardcoded: roles, menus and the permission catalogue all come from
 * the tenant's own tables, so a new module shows up here the moment it is seeded.
 *
 * resourceType is fixed to '*' on purpose. AuthzService.evaluate() matches on the
 * permission code alone and ignores resource_type entirely, so any other value
 * would only create rows that look meaningful and are not.
 */

/** Groups a permission under its module, taken from the code itself. */
function moduleOf(p: Permission): string {
  const prefix = p.permissionCode.split('_')[0];
  return /^M\d+$/.test(prefix) ? prefix : (p.permissionCategory || 'GENERAL');
}

/**
 * Matches on word boundaries, so typing "rate" offers Rate Master and not
 * every GENERATE and MODERATE that merely contains those four letters.
 * A query with a space is matched as typed, so "rate master" still works.
 */
export function matchesSearch(text: string, upperQuery: string): boolean {
  if (!upperQuery) return true;
  const hay = text.toUpperCase();
  if (upperQuery.includes(' ')) return hay.includes(upperQuery);
  return hay.split(/[^A-Z0-9]+/).some((word) => word.startsWith(upperQuery));
}

export function RoleRightsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [menus, setMenus] = useState<readonly MenuItem[]>([]);
  const [permissions, setPermissions] = useState<readonly Permission[]>([]);
  const [roleId, setRoleId] = useState('');

  // What the server currently holds, and what the user has ticked.
  const [savedMenus, setSavedMenus] = useState<ReadonlySet<string>>(new Set());
  const [savedPerms, setSavedPerms] = useState<ReadonlyMap<string, string>>(new Map());
  const [draftMenus, setDraftMenus] = useState<ReadonlySet<string>>(new Set());
  const [draftPerms, setDraftPerms] = useState<ReadonlySet<string>>(new Set());

  const [search, setSearch] = useState('');
  const [copyFrom, setCopyFrom] = useState('');
  const [copyReplace, setCopyReplace] = useState(false);
  const [copying, setCopying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingRole, setLoadingRole] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [roleList, menuList, permList] = await Promise.all([
          listRoles(token),
          listMenus(false, token),
          listPermissions(token),
        ]);
        if (!alive) return;
        setRoles(roleList);
        setMenus(menuList);
        setPermissions(permList);
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    if (!roleId) {
      setSavedMenus(new Set()); setDraftMenus(new Set());
      setSavedPerms(new Map()); setDraftPerms(new Set());
      return;
    }
    setLoadingRole(true);
    setError(null);
    setNotice(null);
    (async () => {
      try {
        const [menuMaps, permMaps] = await Promise.all([
          listRoleMenus(roleId, token),
          getRolePermissions(roleId, token),
        ]);
        if (!alive) return;
        const m = new Set(menuMaps.map((x) => x.menuId));
        const p = new Map(permMaps.map((x) => [x.permissionId, x.mapId]));
        setSavedMenus(m); setDraftMenus(new Set(m));
        setSavedPerms(p); setDraftPerms(new Set(p.keys()));
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoadingRole(false);
      }
    })();
    return () => { alive = false; };
  }, [roleId, token]);

  const permsByModule = useMemo(() => {
    const q = search.trim().toUpperCase();
    const groups = new Map<string, Permission[]>();
    for (const p of permissions) {
      if (
        q &&
        !matchesSearch(p.permissionCode, q) &&
        !matchesSearch(p.permissionName ?? '', q)
      )
        continue;
      const key = moduleOf(p);
      if (!groups.has(key)) groups.set(key, []);
      (groups.get(key) as Permission[]).push(p);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, list]) => ({
        code,
        list: list.slice().sort((x, y) => x.permissionCode.localeCompare(y.permissionCode)),
      }));
  }, [permissions, search]);

  const visibleMenus = useMemo(() => {
    const q = search.trim().toUpperCase();
    return menus
      .filter(
        (m) =>
          !q || matchesSearch(m.menuCode, q) || matchesSearch(m.menuName, q),
      )
      .slice()
      .sort((a, b) => a.menuCode.localeCompare(b.menuCode));
  }, [menus, search]);

  const pending = useMemo(() => {
    const menuAdd = [...draftMenus].filter((id) => !savedMenus.has(id));
    const menuDrop = [...savedMenus].filter((id) => !draftMenus.has(id));
    const permAdd = [...draftPerms].filter((id) => !savedPerms.has(id));
    const permDrop = [...savedPerms.keys()].filter((id) => !draftPerms.has(id));
    return { menuAdd, menuDrop, permAdd, permDrop,
             count: menuAdd.length + menuDrop.length + permAdd.length + permDrop.length };
  }, [draftMenus, savedMenus, draftPerms, savedPerms]);

  function toggle(set: ReadonlySet<string>, id: string, on: boolean): ReadonlySet<string> {
    const next = new Set(set);
    if (on) next.add(id); else next.delete(id);
    return next;
  }

  /** Re-reads what the server holds for the selected role. */
  async function reload() {
    const [menuMaps, permMaps] = await Promise.all([
      listRoleMenus(roleId, token),
      getRolePermissions(roleId, token),
    ]);
    const m = new Set(menuMaps.map((x) => x.menuId));
    const p = new Map(permMaps.map((x) => [x.permissionId, x.mapId]));
    setSavedMenus(m); setDraftMenus(new Set(m));
    setSavedPerms(p); setDraftPerms(new Set(p.keys()));
  }

  async function onCopy() {
    if (!roleId || !copyFrom) return;
    setCopying(true);
    setError(null);
    setNotice(null);
    try {
      const res = await copyRoleRights(roleId, copyFrom, copyReplace, token);
      await reload();
      clearMenuCache();
      setNotice(
        t('roles.rights.copied', {
          perms: String(res.permissionsAdded),
          menus: String(res.menusAdded),
        }),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCopying(false);
    }
  }

  async function onSave() {
    if (!roleId || pending.count === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      for (const menuId of pending.menuAdd) {
        await assignMenuRole(menuId, roleId, { canView: true }, token);
      }
      for (const menuId of pending.menuDrop) {
        await revokeMenuRole(menuId, roleId, token);
      }
      for (const permissionId of pending.permAdd) {
        await assignPermission(roleId, permissionId, '*', token);
      }
      for (const permissionId of pending.permDrop) {
        const mapId = savedPerms.get(permissionId);
        if (mapId) await revokePermission(roleId, mapId, token);
      }
      // Re-read rather than assume the writes landed exactly as ticked.
      await reload();
      // Holders of this role pick the change up on their next menu fetch; this
      // drops the copy cached in THIS browser so the shell updates immediately.
      clearMenuCache();
      setNotice(t('roles.rights.saved'));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>{t('roles.rights.loading')}</p>;

  return (
    <main aria-label="role-rights">
      <h1>{t('roles.rights.title')}</h1>
      <p>{t('roles.rights.intro')}</p>

      <label htmlFor="rr-role">{t('roles.rights.role')}</label>
      <select
        id="rr-role"
        value={roleId}
        onChange={(e) => setRoleId(e.target.value)}
        aria-label={t('roles.rights.role')}
      >
        <option value="">{t('roles.rights.rolePlaceholder')}</option>
        {roles.map((r) => (
          <option key={r.roleId} value={r.roleId}>
            {r.roleName} ({r.roleCode})
          </option>
        ))}
      </select>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {roleId && !loadingRole ? (
        <>
          <section aria-label={t('roles.rights.copyHeading')}>
            <h2>{t('roles.rights.copyHeading')}</h2>
            <p>{t('roles.rights.copyHint')}</p>
            <label htmlFor="rr-copy-from">{t('roles.rights.copyFrom')}</label>
            <select
              id="rr-copy-from"
              value={copyFrom}
              onChange={(e) => setCopyFrom(e.target.value)}
              aria-label={t('roles.rights.copyFrom')}
            >
              <option value="">{t('roles.rights.rolePlaceholder')}</option>
              {roles
                .filter((r) => r.roleId !== roleId)
                .map((r) => (
                  <option key={r.roleId} value={r.roleId}>
                    {r.roleName} ({r.roleCode})
                  </option>
                ))}
            </select>
            <label>
              <input
                type="checkbox"
                checked={copyReplace}
                onChange={(e) => setCopyReplace(e.target.checked)}
                aria-label={t('roles.rights.copyReplace')}
              />
              {t('roles.rights.copyReplace')}
            </label>
            <Button
              type="button"
              variant="secondary"
              onClick={onCopy}
              disabled={copying || !copyFrom}
              loading={copying}
              data-testid="rr-copy"
            >
              {t('roles.rights.copyAction')}
            </Button>
          </section>

          <label htmlFor="rr-search">{t('roles.rights.search')}</label>
          <input
            id="rr-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('roles.rights.searchHint')}
          />

          <Button
            type="button"
            variant="primary"
            onClick={onSave}
            disabled={saving || pending.count === 0}
            loading={saving}
            data-testid="rr-save"
          >
            {pending.count === 0
              ? t('roles.rights.noChanges')
              : t('roles.rights.save', { count: String(pending.count) })}
          </Button>

          <h2>{t('roles.rights.menusHeading')}</h2>
          <p>{t('roles.rights.menusHint')}</p>
          <DataTable<MenuItem>
            testId="rr-menus"
            ariaLabel={t('roles.rights.menusHeading')}
            rows={visibleMenus}
            rowKey={(m) => m.menuId}
            columns={[
              {
                key: 'show',
                header: t('roles.rights.col.show'),
                render: (m) => (
                  <input
                    type="checkbox"
                    aria-label={`menu ${m.menuCode}`}
                    checked={draftMenus.has(m.menuId)}
                    onChange={(e) => setDraftMenus(toggle(draftMenus, m.menuId, e.target.checked))}
                  />
                ),
              },
              { key: 'menuName', header: t('roles.rights.col.menu'), render: (m) => m.menuName },
              { key: 'menuCode', header: t('roles.rights.col.code'), render: (m) => m.menuCode },
            ]}
          />

          <h2>{t('roles.rights.permsHeading')}</h2>
          <p>{t('roles.rights.permsHint')}</p>
          {permsByModule.map((g) => (
            <section key={g.code} aria-label={`module ${g.code}`}>
              <h3>{g.code}</h3>
              <ul>
                {g.list.map((p) => (
                  <li key={p.permissionId}>
                    <label>
                      <input
                        type="checkbox"
                        aria-label={p.permissionCode}
                        checked={draftPerms.has(p.permissionId)}
                        onChange={(e) =>
                          setDraftPerms(toggle(draftPerms, p.permissionId, e.target.checked))
                        }
                      />
                      {p.permissionCode}
                      {p.permissionName && p.permissionName !== p.permissionCode
                        ? ` — ${p.permissionName}`
                        : ''}
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      ) : null}

      {roleId && loadingRole ? <p>{t('roles.rights.loadingRole')}</p> : null}
    </main>
  );
}
